"use client";

import React, { useState, useEffect, useCallback, useMemo, useRef } from "react";
import {
  CopyIcon,
  TrashIcon,
  CheckIcon,
  FrameCornersIcon,
} from "@phosphor-icons/react";
import {
  useOptionalToolsSharedCaseData,
  sharedDataCasesMatch,
  stripWebpToRawBase64,
  MAX_CAPTURE_BASE64_CHARS,
  MAX_CAPTURES,
  type ToolsSharedCaptureEntry,
} from "./tools-shared-case-data";
import { useToolsDrawer } from "./tools-drawer";
import { Button } from "@tranquility-ai/theme-components/button";
import { useTimePilotT } from "@/i18n/useTimePilotT";
import {
  captureEntryDisplayUrl,
  captureEntryImageRef,
} from "@/shared/userImageStorage";
import dayjs from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";

dayjs.extend(relativeTime);

/** @deprecated Use captureEntryDisplayUrl from userImageStorage. */
export function webpEntryToDataUrl(entry: ToolsSharedCaptureEntry): string {
  return captureEntryDisplayUrl(entry);
}

function captureEntryMetaLabel(entry: ToolsSharedCaptureEntry): string {
  if (entry.s3Uri?.trim()) {
    const tail = entry.s3Uri.split("/").pop();
    return tail ? `S3 · ${tail}` : "S3";
  }
  if (entry.webpBase64) {
    return `${(entry.webpBase64.length / 1024).toFixed(0)} KB b64`;
  }
  return "Capture";
}

/** Data URL for the obfuscated capture variant, or `null` if none is stored. */
export function obfuscatedWebpEntryToDataUrl(entry: ToolsSharedCaptureEntry): string | null {
  return entry.obfuscatedWebpBase64
    ? `data:image/webp;base64,${entry.obfuscatedWebpBase64}`
    : null;
}

/** Parse pasted text into raw base64; returns null if empty or too large. */
export function parseWebpCaptureInput(text: string): string | null {
  const raw = stripWebpToRawBase64(text);
  if (!raw) return null;
  if (raw.length > MAX_CAPTURE_BASE64_CHARS) return null;
  return raw;
}

export function useCapturesState(caseId: string | undefined) {
  const remote = useOptionalToolsSharedCaseData();
  const caseBound = Boolean(
    caseId &&
      remote &&
      sharedDataCasesMatch(caseId, remote.caseId, remote.tenantFriendlyName),
  );
  const apiActive = caseBound && Boolean(remote?.hydrated);
  const setCaptures = caseBound ? remote!.setCaptures : null;

  const captures = caseBound && remote!.hydrated ? remote!.captures : [];

  const addCapture = useCallback(
    async (imageData: string) => {
      if (!setCaptures) return false;
      const raw = stripWebpToRawBase64(imageData);
      if (!raw) return false;
      // Paste has no parent object. Junkdrawer requires sourceKey (and origin
      // MD5 of that object), so persist the bytes in shared data instead of
      // uploading. Captures from media still go through uploadUserImage.
      if (raw.length > MAX_CAPTURE_BASE64_CHARS) return false;
      setCaptures((prev) => {
        const entry: ToolsSharedCaptureEntry = {
          id: crypto.randomUUID(),
          s3Uri: "",
          webpBase64: raw,
          createdAt: Date.now(),
        };
        return [entry, ...prev].slice(0, MAX_CAPTURES);
      });
      return true;
    },
    [setCaptures],
  );

  const removeCapture = useCallback(
    (id: string) => {
      if (!setCaptures) return;
      setCaptures((prev) => prev.filter((c) => c.id !== id));
    },
    [setCaptures],
  );

  const remoteRefetch = remote?.refetch;
  const refetch = useCallback(() => {
    if (apiActive && remoteRefetch) {
      void remoteRefetch();
    }
  }, [apiActive, remoteRefetch]);

  return useMemo(
    () => ({
      captures,
      addCapture,
      removeCapture,
      refetch,
      /** True when captures load/save via `/api/protected/sharedData` for the open case. */
      apiActive,
    }),
    [captures, addCapture, removeCapture, refetch, apiActive],
  );
}

export interface ToolsCapturesContentProps {
  captures: ToolsSharedCaptureEntry[];
  onAddCapture: (imageData: string) => Promise<boolean>;
  onRemoveCapture: (id: string) => void;
  /** When false, list is empty and changes are not persisted (no open case). */
  apiActive: boolean;
}

export function ToolsCapturesContent({
  captures,
  onAddCapture,
  onRemoveCapture,
  apiActive,
}: ToolsCapturesContentProps) {
  const t = useTimePilotT();
  const toolsDrawer = useToolsDrawer();
  const applyCaptureToEventMedia = toolsDrawer?.applyCaptureToEventMedia;
  const eventMediaCaptureApplyReady = toolsDrawer?.eventMediaCaptureApplyReady ?? false;

  const [draft, setDraft] = useState("");
  const [addError, setAddError] = useState<string | null>(null);
  const [adding, setAdding] = useState(false);
  const [copiedId, setCopiedId] = useState<string | null>(null);
  const [applyingToEventId, setApplyingToEventId] = useState<string | null>(null);
  const copiedTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const applyingToEventTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const handleAdd = useCallback(async () => {
    setAddError(null);
    if (!apiActive) {
      setAddError("Open a case to save captures to the server.");
      return;
    }
    const parsed = parseWebpCaptureInput(draft);
    if (!parsed) {
      setAddError(
        draft.trim()
          ? "Invalid or too large (max ~200KB image as base64)."
          : "Paste a WebP base64 string or data URL.",
      );
      return;
    }
    setAdding(true);
    try {
      const ok = await onAddCapture(parsed);
      if (ok) setDraft("");
      else setAddError("Could not add capture.");
    } finally {
      setAdding(false);
    }
  }, [draft, onAddCapture, apiActive]);

  const copyDisplayUrl = useCallback((entry: ToolsSharedCaptureEntry) => {
    const url = captureEntryDisplayUrl(entry);
    navigator.clipboard.writeText(url).then(() => {
      if (copiedTimeoutRef.current) clearTimeout(copiedTimeoutRef.current);
      setCopiedId(entry.id);
      copiedTimeoutRef.current = setTimeout(() => {
        setCopiedId(null);
        copiedTimeoutRef.current = null;
      }, 1500);
    });
  }, []);

  const copyImageRef = useCallback((entry: ToolsSharedCaptureEntry) => {
    const ref = captureEntryImageRef(entry);
    if (!ref) return;
    navigator.clipboard.writeText(ref).then(() => {
      if (copiedTimeoutRef.current) clearTimeout(copiedTimeoutRef.current);
      setCopiedId(`${entry.id}-ref`);
      copiedTimeoutRef.current = setTimeout(() => {
        setCopiedId(null);
        copiedTimeoutRef.current = null;
      }, 1500);
    });
  }, []);

  const applyToEventMedia = useCallback(
    (entry: ToolsSharedCaptureEntry) => {
      if (!applyCaptureToEventMedia || !eventMediaCaptureApplyReady) return;
      const imageRef = captureEntryImageRef(entry);
      if (!imageRef) return;
      const ok = applyCaptureToEventMedia(imageRef);
      if (!ok) return;
      if (applyingToEventTimeoutRef.current) clearTimeout(applyingToEventTimeoutRef.current);
      setApplyingToEventId(entry.id);
      applyingToEventTimeoutRef.current = setTimeout(() => {
        setApplyingToEventId(null);
        applyingToEventTimeoutRef.current = null;
      }, 1000);
    },
    [applyCaptureToEventMedia, eventMediaCaptureApplyReady],
  );

  useEffect(() => {
    return () => {
      if (copiedTimeoutRef.current) clearTimeout(copiedTimeoutRef.current);
      if (applyingToEventTimeoutRef.current) clearTimeout(applyingToEventTimeoutRef.current);
    };
  }, []);

  return (
    <div className="flex flex-1 min-h-0 flex-col overflow-hidden">
      <div className="shrink-0 px-3 py-1 border-b border-white/20">
        {!apiActive ? (
          <p className="text-amber-400/90 text-[11px]" role="status">
            {t("toolsCaptures.selectCase")}
          </p>
        ) : null}
      </div>
      <div className="flex-1 min-h-0 overflow-y-auto">
        {captures.length === 0 ? (
          <p className="px-3 py-4 text-white/50 text-xs">
            {apiActive ? t("toolsCaptures.none") : t("toolsCaptures.noCase")}
          </p>
        ) : (
          <ul className="flex flex-col gap-0 list-none pl-0">
            {captures.map((entry) => (
              <li
                key={entry.id}
                className="flex flex-col gap-2 border-b border-white/10 px-3 py-1 sm:flex-row sm:items-start"
              >
                <div className="shrink-0 rounded border border-white/10 overflow-hidden bg-black/40 w-full max-w-[200px] aspect-video flex items-center justify-center">
                  <img
                    src={captureEntryDisplayUrl(entry)}
                    alt=""
                    className="max-w-full max-h-[120px] w-auto h-auto object-contain"
                  />
                </div>
                <div className="min-w-0 flex-1 flex flex-col gap-1">
                  <p className="text-white/50 text-[11px]">
                    {dayjs(entry.createdAt).fromNow()} ·{" "}
                    <span className="text-white/40 font-mono text-[10px]">
                      {captureEntryMetaLabel(entry)}
                    </span>
                  </p>
                  <div className="flex flex-wrap gap-1">
                    {copiedId === entry.id ? (
                      <span className="inline-flex items-center gap-1 rounded px-1.5 py-0.5 text-[11px] text-white/70">
                        <CheckIcon size={14} weight="bold" />
                        {t("toolsCaptures.copiedUrl")}
                      </span>
                    ) : (
                      <Button
                        type="button"
                        size="sm"
                        style="text"
                        color="light"
                        background="inverse"
                        leadingIcon={<CopyIcon weight="duotone" />}
                        onClick={() => copyDisplayUrl(entry)}
                        className="text-[11px] px-2 py-0.5"
                      >
                        {t("favorites.copyDisplayUrl")}
                      </Button>
                    )}
                    {copiedId === `${entry.id}-ref` ? (
                      <span className="inline-flex items-center gap-1 rounded px-1.5 py-0.5 text-[11px] text-white/70">
                        <CheckIcon size={14} weight="bold" />
                        {t("toolsCaptures.copiedRef")}
                      </span>
                    ) : (
                      <Button
                        type="button"
                        size="sm"
                        style="text"
                        color="light"
                        background="inverse"
                        leadingIcon={<CopyIcon weight="duotone" />}
                        onClick={() => copyImageRef(entry)}
                        className="text-[11px] px-2 py-0.5"
                      >
                        {t("toolsCaptures.copyImageRef")}
                      </Button>
                    )}
                    <Button
                      type="button"
                      size="sm"
                      style="text"
                      color="light"
                      background="inverse"
                      disabled={!eventMediaCaptureApplyReady}
                      title={
                        eventMediaCaptureApplyReady
                          ? t("toolsCaptures.applyReady")
                          : t("toolsCaptures.applyNeedsEvent")
                      }
                      onClick={() => applyToEventMedia(entry)}
                      className="text-[11px] px-2 py-0.5"
                      leadingIcon={
                        <span className="relative inline-flex items-center">
                          <FrameCornersIcon
                            weight="duotone"
                            className={
                              applyingToEventId === entry.id ? "text-green-500" : "text-white/70"
                            }
                          />
                          <span className="absolute -right-1 -top-0.5 text-[9px] text-white/70 leading-none">
                            +
                          </span>
                        </span>
                      }
                    >
                      {t("toolsCaptures.toEventMedia")}
                    </Button>
                    <Button
                      type="button"
                      size="sm"
                      style="text"
                      color="danger"
                      background="inverse"
                      leadingIcon={<TrashIcon weight="duotone" />}
                      onClick={() => onRemoveCapture(entry.id)}
                      className="text-[11px] px-2 py-0.5 ml-auto sm:ml-0"
                      aria-label={t("toolsCaptures.removeCapture")}
                    >
                      {t("caseFiles.remove")}
                    </Button>
                  </div>
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}
